import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'

export enum OnboardingTypeEnum {
  INDIVIDUAL = 'INDIVIDUAL',
  JOINT = 'JOINT',
  COMPANY = 'COMPANY',
  IRA = 'IRA',
  TRUST = 'TRUST',
}

export enum CROWDFUNDING_TYPE {
  REG_A = 'REG_A',
  REG_CF = 'REG_CF',
}

export const useRiseUpAPIV1 = () => {
  const RISE_UP_API_V1 = process.env.REACT_APP_RISE_UP_API_V1

  const getIPAddress = async () => {
    try {
      const response = await axios.get('https://api.ipify.org?format=json')

      return response.data.ip
    } catch (error) {
      console.error('Error fetching the IP address:', error)
    }
  }

  const checkEmailStatus = async (email: string) => {
    const request: AxiosRequestConfig = {
      method: 'post',
      url: `${RISE_UP_API_V1}/user-onboarding/pre-onboard/${encodeURIComponent(email)}`,
      headers: {
        accept: '*/*',
        'Content-Type': 'application/json',
      },
    }

    let res: AxiosResponse
    res = await axios(request)
    return res
  }

  const onboardUserEmail = async (
    email: string,
    onboardingType: OnboardingTypeEnum,
    appliedOnboardingGroupLink?: string,
  ) => {
    const request: AxiosRequestConfig = {
      method: 'post',
      url: `${RISE_UP_API_V1}/user-onboarding/onboard/${encodeURIComponent(email)}`,
      headers: {
        accept: '*/*',
        'Content-Type': 'application/json',
      },
      data: {
        onboardingType,
        crowdfundingType: CROWDFUNDING_TYPE.REG_A,
        appliedOnboardingGroupLink,
      },
    }

    let res: AxiosResponse
    res = await axios(request)
    return res
  }

  const sendOnboardingURL = async (email: string) => {
    const request: AxiosRequestConfig = {
      method: 'post',
      url: `${RISE_UP_API_V1}/user-onboarding/send-onboarding-url/${encodeURIComponent(email)}`,
      headers: {
        accept: '*/*',
        'Content-Type': 'application/json',
      },
    }

    let res: AxiosResponse
    res = await axios(request)
    return res
  }

  const getOnboardProgress = async (email: string) => {
    const request: AxiosRequestConfig = {
      method: 'get',
      url: `${RISE_UP_API_V1}/user-onboarding/onboard-progress/${encodeURIComponent(email)}`,
      headers: {
        accept: '*/*',
        'Content-Type': 'application/json',
        'x-custom-lang': 'en',
      },
    }

    let res: AxiosResponse
    res = await axios(request)
    return res
  }

  const updateOnboardProgress = async (data: any) => {
    const request: AxiosRequestConfig = {
      method: 'patch',
      url: `${RISE_UP_API_V1}/user-onboarding/onboard-progress/${encodeURIComponent(data.onboardingId)}`,
      headers: {
        accept: '*/*',
        'Content-Type': 'application/json',
        'x-custom-lang': 'en',
      },
      data,
    }

    let res: AxiosResponse
    res = await axios(request)
    return res
  }

  const createACHBankAccount = async (data: any, bearer_token: string) => {
    const request: AxiosRequestConfig = {
      method: 'post',
      url: `${RISE_UP_API_V1}/user-payments/create-ach-bank-account`,
      headers: {
        accept: '*/*',
        'Content-Type': 'application/json',
        'x-custom-lang': 'en',
        Authorization: `bearer ${bearer_token}`,
      },
      data,
    }

    let res: AxiosResponse
    res = await axios(request)
    return res
  }

  const sendACHPaymentToOffering = async (data: any, bearer_token: string) => {
    const request: AxiosRequestConfig = {
      method: 'post',
      url: `${RISE_UP_API_V1}/user-payments/send-ach-payment-to-offering`,
      headers: {
        accept: '*/*',
        'Content-Type': 'application/json',
        'x-custom-lang': 'en',
        Authorization: `bearer ${bearer_token}`,
      },
      data,
    }

    let res: AxiosResponse

    res = await axios(request)
    return res
  }

  const createOnboardingId = async (email: string) => {
    const request: AxiosRequestConfig = {
      method: 'post',
      url: `${RISE_UP_API_V1}/user-onboarding/onboard-progress/${encodeURIComponent(email)}`,
      headers: {
        accept: '*/*',
        'Content-Type': 'application/json',
        'x-custom-lang': 'en',
      },
    }

    let res: AxiosResponse
    res = await axios(request)
    return res
  }

  const loginInvestor = async (data: any) => {
    const request: AxiosRequestConfig = {
      method: 'post',
      url: `${RISE_UP_API_V1}/user/login`,
      headers: {
        accept: '*/*',
        'Content-Type': 'application/json',
        'x-custom-lang': 'en',
      },
      data: {
        username: data.username,
        rememberMe: false,
        password: data.password,
      },
    }

    let res: AxiosResponse
    res = await axios(request)
    return res
  }

  const performIdologyKyc = async (bearer_token: string) => {
    const request: AxiosRequestConfig = {
      method: 'post',
      url: `${RISE_UP_API_V1}/user/perform-idology-kyc-expectid`,
      headers: {
        accept: '*/*',
        'Content-Type': 'application/json',
        'x-custom-lang': 'en',
        Authorization: `bearer ${bearer_token}`,
      },
    }

    let res: AxiosResponse
    res = await axios(request)
    return res
  }

  const finalizeUserCreation = async (onboardingId: string) => {
    const request: AxiosRequestConfig = {
      method: 'post',
      url: `${RISE_UP_API_V1}/user/finalize-user-creation/${encodeURIComponent(onboardingId)}`,
      headers: {
        accept: '*/*',
        'Content-Type': 'application/json',
        'x-custom-lang': 'en',
      },
    }

    let res: AxiosResponse
    res = await axios(request)
    return res
  }

  const sendWirePaymentReminderEmail = async (onboardingId: string, tradeId: string) => {
    const request: AxiosRequestConfig = {
      method: 'post',
      url: `${RISE_UP_API_V1}/user-onboarding/send-wire-payment-reminder-email/${encodeURIComponent(
        onboardingId,
      )}/${encodeURIComponent(tradeId)}`,
      headers: {
        accept: '*/*',
        'Content-Type': 'application/json',
        'x-custom-lang': 'en',
      },
    }

    let res: AxiosResponse
    res = await axios(request)
    return res
  }

  const updateOnboardingType = async (
    email: string,
    onboardingType: OnboardingTypeEnum,
    appliedOnboardingGroupLink?: string,
  ) => {
    const request: AxiosRequestConfig = {
      method: 'patch',
      url: `${RISE_UP_API_V1}/user-onboarding/onboarding-type/${encodeURIComponent(email)}`,
      headers: {
        accept: '*/*',
        'Content-Type': 'application/json',
        'x-custom-lang': 'en',
      },
      data: {
        onboardingType,
        crowdfundingType: CROWDFUNDING_TYPE.REG_A,
        appliedOnboardingGroupLink,
      },
    }

    let res: AxiosResponse
    res = await axios(request)
    return res
  }

  const saveInvestorSignDate = async (bearer_token: string) => {
    const request: AxiosRequestConfig = {
      method: 'post',
      url: `${RISE_UP_API_V1}/user-onboarding/save-investor-signed-subscription-agreement-date`,
      headers: {
        accept: '*/*',
        'Content-Type': 'application/json',
        'x-custom-lang': 'en',
        Authorization: `bearer ${bearer_token}`,
      },
    }

    let res: AxiosResponse
    res = await axios(request)
    return res
  }

  const getUserStatus = async (email: string) => {
    const request: AxiosRequestConfig = {
      method: 'get',
      url: `${RISE_UP_API_V1}/user/get-user-status/${encodeURIComponent(email)}`,
    }

    let res: AxiosResponse
    res = await axios(request)
    return res
  }

  const sendSetPasswordEmail = async (email: string) => {
    const request: AxiosRequestConfig = {
      method: 'post',
      url: `${RISE_UP_API_V1}/user-onboarding/send-set-password-email/${encodeURIComponent(email)}`,
      headers: {
        accept: '*/*',
        'Content-Type': 'application/json',
      },
    }

    let res: AxiosResponse
    res = await axios(request)
    return res
  }

  const verifyReturningInvestor = async (data: { email: string; birthYear: number; ssnDigits: {} }) => {
    const request: AxiosRequestConfig = {
      method: 'post',
      url: `${RISE_UP_API_V1}/user/verify-returning-investor`,
      headers: {
        accept: '*/*',
        'Content-Type': 'application/json',
      },
      data,
    }

    let res: AxiosResponse
    res = await axios(request)
    return res
  }

  return {
    checkEmailStatus,
    onboardUserEmail,
    getOnboardProgress,
    updateOnboardProgress,
    sendOnboardingURL,
    createOnboardingId,
    createACHBankAccount,
    sendACHPaymentToOffering,
    loginInvestor,
    performIdologyKyc,
    finalizeUserCreation,
    sendWirePaymentReminderEmail,
    updateOnboardingType,
    saveInvestorSignDate,
    getIPAddress,
    getUserStatus,
    sendSetPasswordEmail,
    verifyReturningInvestor,
  }
}
